import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Form } from 'react-final-form';
import api from '@/api';
// styles
import styles from './styles.module.scss';
// images
import {
  BackArrow,
  ForwardArrow,
} from '@/components/landing-page-templates/ConfigLandingPage/LandingPageIcons';
// components
import Loader from '@/components/ui/Loader';
import FormSection from '@/components/forms/FormSection';
import FormValidationErrorTooltip from '@/components/ui/FormValidationErrorTooltip';
import { User } from 'react-feather';
// config
import { FORM_FIELDS } from './config';
// types
import {
  IAccountRegistrationEnum,
  IAccountStatusOptions,
} from '@/models/account/types';

const EnterPasswordForm = ({
  buttonStyle,
  updateLoginMethod,
  email,
  setNotificationMode,
  isSpeakerPage,
  isSponsorLandingPage,
}) => {
  const dispatch = useDispatch();
  const { eventId } = useParams<{ eventId: string }>();

  const [loading, setLoading] = useState(false);
  const [isPasswordCorrect, setIsPasswordCorrect] = useState<boolean>(true);

  const [isAccountDeactivated, setIsAccountDeactivated] = useState<boolean>(
    false,
  );

  // Set the password validation state if the user starts to edit the field (removing
  //  the warning border and form validation message)
  const handleOnChange = () => {
    if (!isPasswordCorrect) {
      setIsPasswordCorrect(true);
    }
  };

  // The handler for clicking the button or when Enter key is pressed
  const handleFormSubmit = ({ password }) => {
    setLoading(true);
    const isAttendeeRegistration = isSpeakerPage ? false : true;
    api.account
      .login_landingPage(
        {
          email: email,
          password: password,
        },
        eventId,
        isAttendeeRegistration,
      )
      .then(({ data }) => {
        setLoading(false);
        dispatch({ type: 'account/setAccount' });
        dispatch({ type: 'account/setUserData', payload: { data } });
        dispatch({
          type: 'account/setRegistrationStep',
          payload: { registrationStep: IAccountRegistrationEnum.COMPLETE },
        });
      })
      .catch(e => {
        setLoading(false);
        if (
          e.response.status === 401 &&
          e?.response?.data?.message ===
            IAccountStatusOptions.DEACTIVATED_ACCOUNT
        ) {
          setNotificationMode(true);
          setIsAccountDeactivated(true);
          return;
        }
        if (
          e.response.status === 401 &&
          e?.response?.data?.message ===
            IAccountStatusOptions.INVALID_CREDENTIALS
        ) {
          // This will show the inline form validation error "Password is incorrect"
          setIsPasswordCorrect(false);
          return;
        }
        // This will show the inline form validation error "Password is incorrect"
        setIsPasswordCorrect(false);
      });
  };

  const handleBackButton = () => {
    updateLoginMethod(undefined);
    // Set the registration step
    dispatch({
      type: 'account/setRegistrationStep',
      payload: { registrationStep: IAccountRegistrationEnum.LOGIN_METHOD },
    });
  };

  // The handler for a click on the back arrow
  const handleBackClick = () => {
    handleBackButton();
  };

  // The handler for the Escape key which goes back to the login screen
  const handleBackEnterKey = event => {
    if (event.key === 'Enter') {
      handleBackButton();
    }
  };

  const handlePasswordReset = () => {
    dispatch({
      type: 'account/setRegistrationStep',
      payload: { registrationStep: IAccountRegistrationEnum.RESET_PASSWORD },
    });
  };

  return (
    <div>
      <div className={styles.sectionHeader}>
        {/* Back Arrow SVG */}
        <div
          role="button"
          tabIndex={-1}
          className={styles.backArrow}
          onClick={handleBackClick}
          onKeyPress={handleBackEnterKey}
        >
          <BackArrow />
        </div>
        <p className={styles.sectionLabel}>Enter Your Password</p>
      </div>
      <Form
        onSubmit={handleFormSubmit}
        render={({ handleSubmit, submitting }) => (
          <form onSubmit={handleSubmit} onChange={handleOnChange}>
            <div className={styles.emailContainer}>
              <div className={styles.userIcon}>
                <User width="16px" />
              </div>
              <div className={styles.emailLabel}>{email}</div>
            </div>
            <FormSection
              formData={FORM_FIELDS}
              styleClass={
                isPasswordCorrect
                  ? 'landingPageFormSection'
                  : 'passwordFormSectionValidationError'
              }
            />
            {!isPasswordCorrect && (
              <div className={styles.passwordIncorrectMessage}>
                <FormValidationErrorTooltip value="Incorrect Password" />
              </div>
            )}

            <div className={styles.forgotPassword}>
              <div
                onClick={handlePasswordReset}
                onKeyPress={handlePasswordReset}
                tabIndex={-1}
                role="button"
              >
                Forgot your password?
              </div>
            </div>
            {isAccountDeactivated && (
              <div className={styles.attendeeDeletionMessage}>
                You have been removed from the event. Please contact the
                organiser to resolve the issue.
              </div>
            )}
            <div className={styles.button}>
              <button
                type="submit"
                onSubmit={handleSubmit}
                onClick={handleSubmit}
                onKeyPress={handleSubmit}
                disabled={submitting}
                style={buttonStyle}
                className={styles.submitButton}
              >
                {/* loader */}
                {loading && <Loader />}
                {/* Forward Arrow SVG */}
                <ForwardArrow color={buttonStyle.color} />
                <p className={styles.buttonText}>Continue</p>
              </button>
            </div>
          </form>
        )}
      />
    </div>
  );
};
export default EnterPasswordForm;
